import { Link } from "react-router-dom";
import { getCollections } from "@/api/endpoints";
import { useApi } from "@/hooks/useApi";
import type { MediaCollection } from "@/types/api";

function tmdbImageSrc(path: string | null | undefined, width: "w342" | "w780" = "w342"): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `https://image.tmdb.org/t/p/${width}${path.startsWith("/") ? path : `/${path}`}`;
}

function CollectionPoster({ collection }: { collection: MediaCollection }) {
  const posterSrc = tmdbImageSrc(collection.attributes.poster_path, "w342");
  if (posterSrc) {
    return (
      <img
        className="collection-card-poster"
        src={posterSrc}
        alt={collection.attributes.name}
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <div className="collection-card-poster collection-card-poster--placeholder" aria-hidden="true">
      <span>{collection.attributes.name.slice(0, 2).toUpperCase()}</span>
    </div>
  );
}

export function CollectionsPage() {
  const collections = useApi(() => getCollections(), []);

  if (collections.loading) return <div className="page-loading">Loading...</div>;
  if (collections.error) return <p className="error-message">Collections unavailable</p>;

  const rows = collections.data?.data ?? [];

  return (
    <div className="collections-page">
      <div className="page-header">
        <h1>Collections</h1>
      </div>

      {rows.length > 0 ? (
        <div className="collection-grid">
          {rows.map((collection) => (
            <Link
              key={collection.id}
              className="collection-card"
              to={`/collections/${collection.id}`}
            >
              <CollectionPoster collection={collection} />
              <div className="collection-card-info">
                <span className="collection-card-title">{collection.attributes.name}</span>
                <span className="collection-card-meta">
                  {collection.attributes.item_count.toLocaleString()} movie{collection.attributes.item_count === 1 ? "" : "s"}
                </span>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="library-empty-state">No movie collections are available yet.</div>
      )}
    </div>
  );
}

export function collectionArtworkSrc(path: string | null | undefined, width: "w342" | "w780" | "w1280" = "w780"): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `https://image.tmdb.org/t/p/${width}${path.startsWith("/") ? path : `/${path}`}`;
}
